import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Fragment, type ReactNode } from "react";

import { openExternal } from "~/lib/external-link";

/**
 * One line of counsel's prose, with the addresses in it made tappable.
 *
 * Counsel writes contact details inline — "Contact: info@elyxeer.com", "before the
 * Czech Trade Inspection Authority at www.coi.cz" — and a member who cannot act on
 * them has to copy them out by hand. Marking each one up in the source would mean
 * hand-editing a generated file, so they are recognised here instead.
 *
 * Nothing is ever interpreted as markup: the text is split on the matches and the
 * pieces are returned as React nodes, so a document can only ever produce text and the
 * two link elements below.
 */

/**
 * An e-mail address, or a URL written either with a scheme or as a bare `www.` host.
 * Trailing sentence punctuation is trimmed off the match rather than excluded here —
 * a dot is legal inside both a path and a domain, so only its position tells them apart.
 */
const ADDRESS =
  /([\w.+-]+@[\w-]+\.[\w.-]+)|(https?:\/\/[^\s]+|www\.[\w-]+\.[\w.-]+)/g;

/** Sentence punctuation that swallowed the end of a match. */
const TRAILING = /[.,;:!?)\]]+$/;

/**
 * A mail address.
 *
 * A bare `mailto:`, and the one case `lib/external-link.ts` cannot serve: the in-app
 * browser it opens (SFSafariViewController / Custom Tabs) has no mailto scheme, while
 * both the WebView and a desktop browser hand one straight to the mail client.
 */
function MailLink({ address }: { address: string }) {
  return (
    <Link href={`mailto:${address}`} sx={{ color: "link.main" }}>
      {address}
    </Link>
  );
}

/**
 * A page on somebody else's site.
 *
 * A `<button>`, not an `<a href>`: the WebView has no chrome to come back from a
 * third-party page with, so `openExternal` is what keeps the member inside the app.
 */
function SiteLink({ url, label }: { url: string; label: string }) {
  return (
    <Link
      component="button"
      type="button"
      underline="always"
      sx={{
        color: "link.main",
        // A button does not inherit the paragraph's metrics, and a link that sits a
        // pixel above the baseline in the middle of a sentence reads as a mistake.
        font: "inherit",
        verticalAlign: "baseline",
      }}
      onClick={() => void openExternal(url)}
    >
      {label}
    </Link>
  );
}

export function LegalProse({ children }: { children: string }): ReactNode {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const match of children.matchAll(ADDRESS)) {
    const start = match.index;
    let [matched] = match;
    // Give the sentence back whatever punctuation the match reached into.
    const trailing = TRAILING.exec(matched)?.[0] ?? "";
    if (trailing) matched = matched.slice(0, -trailing.length);
    if (!matched) continue;

    if (start > cursor) nodes.push(children.slice(cursor, start));

    const isEmail = matched.includes("@");
    nodes.push(
      <Fragment key={start}>
        {isEmail ? (
          <MailLink address={matched} />
        ) : (
          <SiteLink
            url={matched.startsWith("http") ? matched : `https://${matched}`}
            label={matched}
          />
        )}
      </Fragment>,
    );
    cursor = start + matched.length;
  }

  if (cursor === 0) return children;
  nodes.push(children.slice(cursor));
  return <>{nodes}</>;
}

/**
 * A clause, with its number set apart from its words.
 *
 * Counsel's paragraphs open with "4.2 " and the prose cross-references those numbers,
 * so they have to stay visible — but run into the sentence they are hard to scan for,
 * which is exactly what someone following a cross-reference is doing.
 */
const CLAUSE = /^(\d+(?:\.\d+)+)\s+(.*)$/s;

export function LegalParagraph({ text }: { text: string }) {
  const clause = CLAUSE.exec(text);

  return (
    <Typography variant="T2" sx={{ color: "text.secondary" }}>
      {clause ? (
        <>
          <Box
            component="span"
            sx={{ color: "text.muted", fontVariantNumeric: "tabular-nums" }}
          >
            {clause[1]}
          </Box>{" "}
          <LegalProse>{clause[2]}</LegalProse>
        </>
      ) : (
        <LegalProse>{text}</LegalProse>
      )}
    </Typography>
  );
}
